import ActionButton from '@atoms/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import { Input } from '@components/ui/input';
import useStores from '@hooks/useStore';
import { useWorkflow } from '@hooks/useWorkflow';
import type { AnalyticsModel } from '@models/analytics.model';
import {
  type JobExecution,
  JobStatus,
  type UpdateJobDto
} from '@stratumn/jobs';
import { Info } from 'lucide-react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import ETLStatusIcon from './AnalyticsStatusIcon';
import DeleteEtlModal from './DeleteEtlModal';
import EditEtlModal from './EditEtlModal';

const AnalyticsMonitor = observer(
  ({ analytics }: { analytics: AnalyticsModel }) => {
    const { workflow } = useWorkflow();
    const [openModalDelete, setOpenModalDelete] = useState(false);
    const [openModalEdit, setOpenModalEdit] = useState(false);
    const [emptyNameError, setEmptyNameError] = useState(false);

    const { analyticsStore } = useStores();
    const [analyticsName, setAnalyticsName] = useState<
      AnalyticsModel['name'] | undefined
    >(analytics?.name);
    const navigate = useNavigate();

    const handleLaunchJob = (analyticsId: string) => {
      if (!workflow) {
        return;
      }
      const promTrigger: Promise<JobExecution | undefined> =
        analyticsStore.triggerEtlJob(analyticsId, workflow);

      toast.promise(promTrigger, {
        loading: 'Triggering analytics...',
        success: loadedAnalyticsJobExecution => {
          if (!loadedAnalyticsJobExecution) {
            return;
          }
          return (
            <span>
              Analytics successfully triggered !
              <br />
              <a
                className="cursor-pointer underline"
                onClick={() =>
                  loadedAnalyticsJobExecution &&
                  navigate(`${analyticsId}/${loadedAnalyticsJobExecution.id}`)
                }
              >
                'Go to ongoing analytics'
              </a>
            </span>
          );
        },
        error: error => {
          if (error instanceof Error) {
            return 'An error occured while generating your analytics: '.concat(
              error.message
            );
          }
          return 'An error occured while generating your analytics: '.concat(
            JSON.stringify(error, Object.getOwnPropertyNames(error))
          );
        }
      });
    };
    const handlePause = async (analyticsId: string) => {
      await analyticsStore.updateEtlJob(analyticsId, {
        status: analytics.status,
        type: 'ETL'
      });
    };
    const handleDelete = async () => {
      await analyticsStore.delete(analytics.id);
    };

    const handleBlur = async () => {
      if (!analyticsName) {
        return setEmptyNameError(true);
      }
      if (emptyNameError) {
        setEmptyNameError(false);
      }
      if (analytics) {
        await analyticsStore.updateEtlJob(analytics.id, {
          name: analyticsName,
          type: 'ETL'
        });
      }
    };

    const handleInputChange = async (field: string, value: string) => {
      const [mainField, subField] = field.split('.') as [
        keyof AnalyticsModel,
        string
      ];
      if (subField) {
        const previousField = { ...analytics };
        const payload = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [mainField]: {
            ...previousField[mainField],
            [subField]: value
          }
        } as UpdateJobDto;
        await analyticsStore.updateEtlJob(analytics.id, payload);
        return;
      }
      await analyticsStore.updateEtlJob(analytics.id, {
        [field]: value,
        type: 'ETL'
      });
    };

    return (
      <>
        <Card className="relative mt-4 overflow-hidden p-0 shadow-md">
          <div className="flex w-full flex-col items-start p-4">
            <CardHeader className="w-full text-start">
              <CardTitle className="flex justify-between">
                <div className="flex gap-4">
                  <Input
                    className={cn(
                      'min-w-70 border-none bg-transparent px-2 py-1 text-gray-800 outline-none hover:bg-gray-100 focus:outline-none focus:ring-0 focus-visible:outline-none',
                      emptyNameError && 'outline outline-1 outline-red-500'
                    )}
                    name="name"
                    onBlur={() => void handleBlur()}
                    onChange={e => setAnalyticsName(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    onKeyUp={e => e.preventDefault()}
                    value={analyticsName}
                  />
                  {emptyNameError && (
                    <div className="flex items-center gap-2 text-red-500 text-xs">
                      <Info size={12} />
                      Analytics name can't be empty
                    </div>
                  )}
                </div>
                <ETLStatusIcon jobStatus={analytics.status} />
              </CardTitle>
              {/* <CronSelector
                isPlain
                name="cron_expression"
                value={analytics.cron_expression ?? ''}
                onCronChange={(e) =>
                  void handleInputChange('cron_expression', e)
                }
                fullWidth={false}
                onClick={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.preventDefault()}
              /> */}
            </CardHeader>

            <CardContent className="w-full p-0 pt-2 text-start text-sm">
              <Accordion collapsible type="single">
                <AccordionItem className="px-2" value="controls">
                  <AccordionTrigger className="p-0">
                    <div className="text-sm">Controls</div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div>
                      <ActionButton
                        onClick={() => {
                          void handleLaunchJob(analytics.id);
                        }}
                        size="sm"
                        value="Trigger job"
                      />

                      <div
                        className="mt-4 ml-4 inline-flex rounded-md shadow-sm"
                        role="group"
                      >
                        <Button
                          className="rounded-none rounded-s-lg font-normal hover:text-blue-700"
                          onClick={() => {
                            setOpenModalEdit(true);
                          }}
                          size="sm"
                          variant={'outline'}
                        >
                          Edit
                        </Button>
                        <Button
                          className="rounded-none border-x-0 font-normal hover:text-blue-700"
                          onClick={() => void handlePause(analytics.id)}
                          size="sm"
                          variant={'outline'}
                        >
                          {analytics.status === JobStatus.ACTIVE
                            ? 'Pause'
                            : 'Resume'}
                        </Button>
                        <Button
                          className="rounded-none rounded-e-lg font-normal text-red-700 hover:text-red-800"
                          onClick={() => setOpenModalDelete(true)}
                          size="sm"
                          variant={'outline'}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </div>
        </Card>
        <DeleteEtlModal
          closeModal={() => setOpenModalDelete(false)}
          onDelete={handleDelete}
          openModal={openModalDelete}
          setOpenModal={setOpenModalDelete}
        />
        <EditEtlModal
          analytics={analytics}
          closeModal={() => setOpenModalEdit(false)}
          handleInputChange={handleInputChange}
          openModal={openModalEdit}
          setOpenModal={setOpenModalEdit}
        />
      </>
    );
  }
);

export default AnalyticsMonitor;
